import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { type OfferVersion } from "../services/api";
import {
  calculateVersionNumber,
  formatPublishedDate,
} from "../utils/versionUtils";

interface OfferVersionSelectorProps {
  versions: OfferVersion[];
  currentOfferId: string;
  currentPublicToken: string;
  currentPublishedDate?: string | null;
  onVersionChange?: (versionId: string) => void;
}

const OfferVersionSelector: React.FC<OfferVersionSelectorProps> = ({
  versions,
  currentOfferId,
  currentPublicToken,
  currentPublishedDate,
  onVersionChange,
}) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentVersionNumber =
    versions.length > 0
      ? calculateVersionNumber(versions, currentOfferId)
      : null;
  const formattedDate = currentPublishedDate
    ? formatPublishedDate(currentPublishedDate)
    : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleVersionChange = (selectedVersionId: string) => {
    const selectedVersion = versions.find((v) => v.id === selectedVersionId);
    if (!selectedVersion) return;

    // If custom handler provided, use it
    if (onVersionChange) {
      onVersionChange(selectedVersionId);
      setShowDropdown(false);
      return;
    }

    // Default navigation behavior
    const latestVersion = versions.sort(
      (a, b) =>
        new Date(b.publishedAt!).getTime() - new Date(a.publishedAt!).getTime()
    )[0];

    if (selectedVersionId === latestVersion.id) {
      navigate(`/offers/public/${currentPublicToken}`);
    } else {
      navigate(
        `/offers/public/${currentPublicToken}/v/${selectedVersion.publicToken}`
      );
    }

    setShowDropdown(false);
  };

  // Don't render if less than 2 versions
  if (versions.length < 2) {
    return null;
  }

  return (
    <div className="my-2 pb-2" ref={dropdownRef}>
      <div className="relative">
        <p className="text-xs text-neutral-500">
          Wersja{" "}
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="font-semibold text-blue-600 hover:text-blue-700 underline decoration-dotted underline-offset-2 focus:outline-none"
          >
            {currentVersionNumber || "?"}
          </button>
          {formattedDate && <span> ({formattedDate})</span>}
        </p>
        {showDropdown && (
          <div className="absolute left-0 mt-2 w-72 bg-white rounded-md shadow-lg border border-neutral-200 z-50">
            <div className="py-1 max-h-64 overflow-y-auto">
              {versions
                .sort(
                  (a, b) =>
                    new Date(b.publishedAt!).getTime() -
                    new Date(a.publishedAt!).getTime()
                )
                .map((version, index) => {
                  const versionNum = calculateVersionNumber(
                    versions,
                    version.id
                  );
                  const isCurrentVersion = version.id === currentOfferId;
                  return (
                    <button
                      key={version.id}
                      onClick={() => handleVersionChange(version.id)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 transition-colors ${
                        isCurrentVersion
                          ? "bg-blue-50 border-l-2 border-blue-500"
                          : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p
                            className={`font-medium ${
                              isCurrentVersion
                                ? "text-blue-700"
                                : "text-neutral-900"
                            }`}
                          >
                            Wersja {versionNum}
                            {index === 0 && (
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                Najnowsza
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {formatPublishedDate(version.publishedAt)}
                          </p>
                        </div>
                        {isCurrentVersion && (
                          <span className="text-blue-600 text-sm">✓</span>
                        )}
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferVersionSelector;
